import { useMemo } from 'react'
import { UseQueryResult } from 'react-query'

import useAuth from 'hooks/use-auth'
import { useGremlinQueries } from 'hooks/use-gremlin-queries'
import { useGremlinQuery } from 'hooks/use-gremlin-query'
import {
  RecommendedScenario,
  Scenario,
  SharedScenario
} from 'types/api/scenarios/scenario'
import { UserSessionTeam } from 'types/api/users/sessions/user-session'
import { scenariosDraftsGet, scenariosGet } from 'utils/endpoints-access'

function createQueriesPerTeam(
  teamsAccess: UserSessionTeam[],
  endpoint: string
) {
  return teamsAccess.map(({ identifier: teamId }) => {
    return {
      endpoint,
      teamId,
      options: {
        placeholderData: [] as Scenario[]
      }
    }
  })
}

function handleQueryResults(queryResult: UseQueryResult<Scenario[]>[]) {
  const reducedQuery = queryResult.reduce(
    (acc, cur) => {
      const { data, isFetching, isError } = cur

      if (data) {
        acc.data = acc.data.concat(data)
      }

      acc.isFetching ||= isFetching
      acc.isError ||= isError

      return acc
    },
    {
      data: [] as Scenario[],
      isFetching: false,
      isError: false
    }
  )

  return reducedQuery
}

export function useGetScenarios() {
  const { hasEndpointAccess, teams } = useAuth()

  const publishedScenariosQueries = useMemo(() => {
    return createQueriesPerTeam(
      teams.filter((team) =>
        hasEndpointAccess(scenariosGet, {
          teamId: team.identifier
        })
      ),
      '/scenarios'
    )
  }, [hasEndpointAccess, teams])

  const draftScenariosQueries = useMemo(() => {
    return createQueriesPerTeam(
      teams.filter((team) =>
        hasEndpointAccess(scenariosDraftsGet, {
          teamId: team.identifier
        })
      ),
      '/scenarios/drafts'
    )
  }, [hasEndpointAccess, teams])

  /**
   * Published and Draft scenarios will need to be aggregated from all teams the current user has access to
   */
  const publishedScenariosRes = useGremlinQueries(
    publishedScenariosQueries
  ) as [UseQueryResult<Scenario[]>]

  const {
    data: publishedScenarios,
    isFetching: isFetchingPublishedScenarios,
    isError: hasErrorPublishedScenarios
  } = useMemo(
    () => handleQueryResults(publishedScenariosRes),
    [publishedScenariosRes]
  )

  const draftScenariosRes = useGremlinQueries(draftScenariosQueries) as [
    UseQueryResult<Scenario[]>
  ]

  const {
    data: draftScenarios,
    isFetching: isFetchingDraftScenarios,
    isError: hasErrorDraftScenarios
  } = useMemo(() => handleQueryResults(draftScenariosRes), [draftScenariosRes])

  /**
   * Shared and Recommended scenarios are static lists that exist for all teams and do not need to be queried for all
   * teams the user has access to.
   */
  const {
    data: sharedScenarios,
    isFetching: isFetchingSharedScenarios,
    isError: hasErrorSharedScenarios
  } = useGremlinQuery<SharedScenario[]>(
    '/scenarios/shared',
    {},
    {
      placeholderData: [] as SharedScenario[]
    }
  )

  const {
    data: recommendedScenarios,
    isFetching: isFetchingRecommendedScenarios,
    isError: hasErrorRecommendedScenarios
  } = useGremlinQuery<RecommendedScenario[]>(
    '/scenarios/recommended',
    {},
    {
      placeholderData: [] as RecommendedScenario[]
    }
  )

  return {
    // publishedScenarios
    publishedScenarios,
    isFetchingPublishedScenarios,
    hasErrorPublishedScenarios,
    // draftScenarios
    draftScenarios,
    isFetchingDraftScenarios,
    hasErrorDraftScenarios,
    // sharedScenarios
    sharedScenarios,
    isFetchingSharedScenarios,
    hasErrorSharedScenarios,
    // recommendedScenarios
    recommendedScenarios,
    isFetchingRecommendedScenarios,
    hasErrorRecommendedScenarios
  }
}
